import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, Copy, KeyRound, Loader2, Power } from "lucide-react";
import { toast } from "sonner";
import { HelpPopover, HelpSection } from "@/components/common/HelpPopover";
import type { ApplyVmIpAddressResult } from "@/types/admin";

/**
 * The fields this dialog reads. Kept to the four the table row already has, so a caller does not
 * need the full `Resource` to open it.
 */
export interface ChangeVmIpTarget {
  name: string;
  status: string;
  publicIpAddress: string | null;
  providerResourceId: string | null;
}

/** Which layer the operator is changing. The whole dialog turns on this one choice. */
export type ChangeVmIpTargetLayer = "record" | "machine";

export type ChangeVmIpSubmit =
  | { target: "record"; ipAddress: string; reason?: string }
  | {
      target: "machine";
      ipAddress: string;
      gateway?: string;
      resetPassword: boolean;
      reason?: string;
    };

interface ChangeVmIpDialogProps {
  /** The VM whose address is being changed; null closes the dialog. */
  resource: ChangeVmIpTarget | null;

  /** Called when the dialog is dismissed. */
  onClose: () => void;

  /** Called with everything the operator chose. The `target` field says which endpoint to call. */
  onSubmit: (payload: ChangeVmIpSubmit) => void;

  /** Whether either mutation is in flight. */
  isSubmitting?: boolean;

  /**
   * The provider's answer, once the machine path has been taken and accepted. Switches the dialog
   * into its result view — which is not optional decoration: when a password was generated, this is
   * the only place it is ever readable.
   */
  result?: ApplyVmIpAddressResult | null;
}

/**
 * Recognises an IPv4 or IPv6 address, with an optional prefix length — the same rules as the
 * backend's `IpAddressInput`, so Save is never offered for a value the endpoint refuses.
 *
 * Two things this rejects that a host-only check lets through. A bare number: the backend's
 * `IPAddress.TryParse` reads "123" as 0.0.0.123. And an unusable prefix (`/abc`, `/0`, `/33`): on the
 * machine path it goes straight into cloud-init and the VM restarts with no working network.
 */
function looksLikeIpAddress(value: string, allowCidr = true): boolean {
  const [address, prefix, ...rest] = value.trim().split("/");
  if (!address || rest.length > 0) return false;
  if (prefix !== undefined && !allowCidr) return false;

  const isIpv6 = address.includes(":");
  const hostOk = isIpv6
    ? /^[0-9a-fA-F:.]+$/.test(address)
    : address.split(".").length === 4 &&
      address
        .split(".")
        .every((octet) => /^\d{1,3}$/.test(octet) && Number(octet) <= 255);
  if (!hostOk) return false;

  if (prefix === undefined) return true;
  if (!/^\d{1,3}$/.test(prefix)) return false;

  const bits = Number(prefix);
  return bits >= 1 && bits <= (isIpv6 ? 128 : 32);
}

/**
 * The statuses the machine endpoint refuses (`RESOURCE_NOT_READY` / `RESOURCE_TERMINATED`), plus
 * `Decommissioning` — a live reinstall in flight, which the backend now refuses with
 * `RESOURCE_REINSTALLING`: the machine is mid-rebuild and cloud-init has nothing stable to write to.
 * The option is disabled with the reason rather than left to come back as a 409 after every field is
 * filled in. The record path has no such restriction.
 */
const MACHINE_BLOCKED_STATUSES = [
  "Provisioning",
  "Pending",
  "Terminating",
  "Terminated",
  "Decommissioning",
];

/**
 * Changes the IP address of a virtual machine, at whichever layer the operator picks.
 *
 * Two jobs behind one entry point, because from the operator's side they are the same sentence
 * ("this VM's address is now X") arrived at from opposite directions:
 *
 * - **WiCloud record only** — the infrastructure team has already re-addressed the VPS by hand and
 *   WiCloud is the only thing still out of date. No provider call, so nothing to schedule and
 *   nothing to restart. Every machine-side field is hidden, because none of them mean anything on
 *   this path.
 * - **Apply to the machine** — cloud-init, through n8n. Writes the address into the VM's own
 *   configuration, optionally with a fresh password in the same trip, and the record is brought
 *   into line once the provider accepts. Needs a restart, which this does not perform.
 *
 * Which one is right is not something the console can infer, hence the choice up front rather than
 * a guess. The copy is kept to one line per option: operators open this dialog repeatedly, and the
 * longer explanation it once carried was read once and then only scrolled past.
 */
export function ChangeVmIpDialog({
  resource,
  onClose,
  onSubmit,
  isSubmitting,
  result,
}: ChangeVmIpDialogProps) {
  const [layer, setLayer] = useState<ChangeVmIpTargetLayer>("record");
  const [ipAddress, setIpAddress] = useState("");
  const [gateway, setGateway] = useState("");
  const [resetPassword, setResetPassword] = useState(false);
  const [reason, setReason] = useState("");

  // Seeded with the address on record whenever a different row is picked, so an operator changing
  // only the host part keeps a CIDR suffix the row already carried — the suffix is stored verbatim
  // and retyping without it would drop it.
  //
  // Defaults to the record path deliberately: it is the one that touches nothing, and a mis-click
  // on the machine path re-addresses a customer's VM.
  useEffect(() => {
    if (resource) {
      setLayer("record");
      setIpAddress(resource.publicIpAddress ?? "");
      setGateway("");
      setResetPassword(false);
      setReason("");
    }
  }, [resource]);

  const open = resource !== null;
  const isMachine = layer === "machine";

  const trimmedIp = ipAddress.trim();
  const trimmedGateway = gateway.trim();

  const isIpValid = looksLikeIpAddress(trimmedIp);

  // A prefix length on a gateway would be nonsense — it names one host, not a network.
  const isGatewayValid =
    !isMachine ||
    trimmedGateway.length === 0 ||
    looksLikeIpAddress(trimmedGateway, false);

  // Only the record path is a no-op when unchanged. On the machine path re-sending the same address
  // is a real request: the machine may not be configured for it yet even though WiCloud says so,
  // which is precisely the drift this dialog exists to resolve.
  const isUnchanged =
    !isMachine && trimmedIp === (resource?.publicIpAddress ?? "").trim();

  // A VM that never reached the provider has no infrastructure behind the address at all — the
  // machine path has nothing to call, and the record is the only thing there is.
  const hasNoProviderId = !resource?.providerResourceId;

  const machineUnavailableReason = hasNoProviderId
    ? "Unavailable — this resource has no machine at the provider."
    : resource?.status === "Decommissioning"
      ? "Unavailable — this VM is being reinstalled."
      : resource && MACHINE_BLOCKED_STATUSES.includes(resource.status)
        ? `Unavailable while the VM is ${resource.status}.`
        : null;

  // The status sync (every five minutes) writes back whatever address the running guest reports, so
  // a record-only correction holds only until the provider disagrees. A Stopped guest reports none,
  // so the sync leaves its record alone. (A locked Running row is skipped too, but the list does not
  // carry `suspendedAt`, so it still gets the warning — rare, and erring on the side of saying it.)
  // Not relevant to the machine path, which makes the provider agree.
  const willBeResynced =
    !isMachine && !hasNoProviderId && resource?.status === "Running";

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied");
    } catch {
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleClose = () => {
    setIpAddress("");
    setGateway("");
    setReason("");
    setResetPassword(false);
    onClose();
  };

  const handleSubmit = () => {
    if (!isIpValid || !isGatewayValid || isUnchanged) return;

    onSubmit(
      isMachine
        ? {
            target: "machine",
            ipAddress: trimmedIp,
            gateway: trimmedGateway || undefined,
            resetPassword,
            reason: reason.trim() || undefined,
          }
        : {
            target: "record",
            ipAddress: trimmedIp,
            reason: reason.trim() || undefined,
          },
    );
  };

  return (
    <Dialog
      open={open}
      // Escape and the corner X are ignored while a write is out. Closing runs the caller's
      // `onClose`, which resets the machine mutation — and a reset detaches a request still in
      // flight, so its answer is never delivered. That answer is the only place a generated password
      // is ever readable: the success toast would fire and the password would be gone.
      onOpenChange={(next) => !next && !isSubmitting && handleClose()}
    >
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Change IP address</DialogTitle>
        </DialogHeader>

        <DialogBody withoutFooter={!!result} className="space-y-4">
          <DialogDescription className="break-words">
            {resource?.name} — currently{" "}
            {resource?.publicIpAddress ?? "no address on record"}.
          </DialogDescription>
          {result ? (
            <ResultView result={result} onCopy={copy} onClose={handleClose} />
          ) : (
            <div className="space-y-4">
              {/* The choice everything else in this dialog turns on. */}
              <div className="space-y-2">
                <Label>Where to apply the change</Label>
                <RadioGroup
                  value={layer}
                  onValueChange={(value: string) =>
                    setLayer(value as ChangeVmIpTargetLayer)
                  }
                  disabled={isSubmitting}
                  className="gap-2"
                >
                  <label
                    htmlFor="change-ip-record"
                    className="flex cursor-pointer items-start gap-3 rounded-lg border p-3"
                  >
                    <RadioGroupItem
                      id="change-ip-record"
                      value="record"
                      className="mt-0.5 cursor-pointer"
                    />
                    <span className="space-y-0.5">
                      <span className="block text-sm font-medium">
                        WiCloud record only
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        Already changed on the VPS. Nothing is sent to the
                        provider.
                      </span>
                    </span>
                  </label>
                  <label
                    htmlFor="change-ip-machine"
                    className="flex cursor-pointer items-start gap-3 rounded-lg border p-3"
                  >
                    <RadioGroupItem
                      id="change-ip-machine"
                      value="machine"
                      disabled={machineUnavailableReason !== null}
                      className="mt-0.5 cursor-pointer"
                    />
                    <span className="space-y-0.5">
                      <span className="block text-sm font-medium">
                        Apply to the machine (cloud-init)
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {machineUnavailableReason ??
                          "Rewrites the VM's network config. Applies at the next restart."}
                      </span>
                    </span>
                  </label>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label htmlFor="change-ip-address">New IP address</Label>
                <Input
                  id="change-ip-address"
                  value={ipAddress}
                  onChange={(event) => setIpAddress(event.target.value)}
                  placeholder="203.0.113.9 or 203.0.113.9/24"
                  className="placeholder:text-placeholder font-mono"
                  disabled={isSubmitting}
                  autoComplete="off"
                  spellCheck={false}
                />
                {/* Only the machine path treats a missing suffix specially; on the record path it
                  is stored as typed, which needs no explaining. */}
                {isMachine && (
                  <p className="text-xs text-muted-foreground">
                    Without a suffix, the machine keeps its current prefix.
                  </p>
                )}
              </div>

              {/* Everything below is meaningless on the record path, so none of it is rendered
                there — a gateway WiCloud never sends and a password nothing would change. */}
              {isMachine && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="change-ip-gateway">
                      Gateway{" "}
                      <span className="text-muted-foreground">(optional)</span>
                    </Label>
                    <Input
                      id="change-ip-gateway"
                      value={gateway}
                      onChange={(event) => setGateway(event.target.value)}
                      placeholder="Keep the current gateway"
                      className="placeholder:text-placeholder font-mono"
                      disabled={isSubmitting}
                      autoComplete="off"
                      spellCheck={false}
                    />
                    {!isGatewayValid && (
                      <p className="text-xs text-destructive-ink">
                        A gateway is a single address, with no CIDR suffix.
                      </p>
                    )}
                  </div>

                  {/* Two options rather than a checkbox: the second changes the customer's login,
                    and that is too consequential to sit as an unlabelled tick beside an address
                    field. */}
                  <div className="space-y-2">
                    <Label>Login password</Label>
                    <RadioGroup
                      value={resetPassword ? "reset" : "keep"}
                      onValueChange={(value: string) =>
                        setResetPassword(value === "reset")
                      }
                      disabled={isSubmitting}
                      className="gap-2"
                    >
                      <label
                        htmlFor="change-ip-keep-password"
                        className="flex cursor-pointer items-center gap-3 rounded-lg border p-3"
                      >
                        <RadioGroupItem
                          id="change-ip-keep-password"
                          value="keep"
                          className="cursor-pointer"
                        />
                        <span className="text-sm font-medium">
                          Keep the current password
                        </span>
                      </label>
                      <label
                        htmlFor="change-ip-reset-password"
                        className="flex cursor-pointer items-start gap-3 rounded-lg border p-3"
                      >
                        <RadioGroupItem
                          id="change-ip-reset-password"
                          value="reset"
                          className="mt-0.5 cursor-pointer"
                        />
                        <span className="space-y-0.5">
                          <span className="block text-sm font-medium">
                            Also set a new password
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            The current password stops working after the
                            restart.
                          </span>
                        </span>
                      </label>
                    </RadioGroup>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="change-ip-reason">
                  Reason{" "}
                  <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Textarea
                  id="change-ip-reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Audit trail only — not shown to the customer"
                  className="placeholder:text-placeholder min-h-16"
                  maxLength={500}
                  disabled={isSubmitting}
                />
              </div>

              {isMachine && (
                <Alert variant="destructive">
                  <Power className="h-4 w-4" />
                  <AlertDescription>
                    <strong>This does not restart the VM.</strong> The new
                    address applies after the next restart.
                  </AlertDescription>
                </Alert>
              )}

              {willBeResynced && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    While the VM is running, the status sync (every 5 minutes)
                    writes the old address back if the infrastructure still
                    reports it.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </DialogBody>
        {/* The result view carries its own way out; the form's buttons stay in view below it. */}
        {!result && (
          <DialogFooter className="gap-2">
            <ChangeIpHelp />
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={
                isSubmitting || !isIpValid || !isGatewayValid || isUnchanged
              }
              title={
                isUnchanged && isIpValid
                  ? "This is the address already on record"
                  : undefined
              }
            >
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isMachine ? "Apply to machine" : "Save"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * What the provider accepted, and the one readable copy of any password it generated.
 *
 * Replaces the form rather than sitting under it: the operator has nothing left to fill in, and a
 * password that can never be read again must not be one scroll below a still-active Save button.
 */
function ResultView({
  result,
  onCopy,
  onClose,
}: {
  result: ApplyVmIpAddressResult;
  onCopy: (value: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="space-y-4">
      <Alert>
        <Power className="h-4 w-4" />
        <AlertTitle>
          {result.alreadyConfigured
            ? `Already configured for ${result.ipAddress}`
            : `Configured for ${result.ipAddress}`}
        </AlertTitle>
        <AlertDescription className="text-foreground">
          Gateway {result.gateway}, on {result.macAddress}.{" "}
          <strong>Restart the VM</strong> to apply — until then it answers on{" "}
          {result.previousIpAddress ?? "its previous address"} only.
        </AlertDescription>
      </Alert>

      {result.recordRevertsUntilRestart && (
        <p className="text-xs text-muted-foreground">
          WiCloud may show the old address again until the restart.
        </p>
      )}

      {result.recordUpdateError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>WiCloud's record was not updated</AlertTitle>
          <AlertDescription>{result.recordUpdateError}</AlertDescription>
        </Alert>
      )}

      {result.newPassword && (
        <Alert className="border-primary/30 bg-primary/5">
          <KeyRound className="h-4 w-4 text-primary" />
          <AlertTitle>New password</AlertTitle>
          <AlertDescription className="text-foreground">
            <p className="mb-3">Copy it now — it is shown only once.</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  value={result.osUsername ?? ""}
                  readOnly
                  className="font-mono"
                />
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  onClick={() => onCopy(result.osUsername ?? "")}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  value={result.newPassword}
                  readOnly
                  className="font-mono"
                />
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  onClick={() => onCopy(result.newPassword!)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {result.passwordResetError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>The password was not reset</AlertTitle>
          <AlertDescription>
            {result.passwordResetError} The address change did happen, so do not
            repeat it.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end">
        <Button type="button" onClick={onClose}>
          Done
        </Button>
      </div>
    </div>
  );
}

/** The short version of `/docs/vm-actions.html#change-ip`. */
function ChangeIpHelp() {
  return (
    <HelpPopover guideHref="/docs/vm-actions.html#change-ip">
      <HelpSection title="WiCloud record only">
        <p>
          Writes the new address on the VM and on the network rows only it owns,
          in one save, plus an admin-only audit row with your reason. Nothing is
          sent to the infrastructure. Use it when the VPS already answers on the
          new address.
        </p>
        <p>
          While the VM is running, the status sync (every 5 minutes) writes the
          old address back if the infrastructure still reports it.
        </p>
      </HelpSection>
      <HelpSection title="Apply to the machine (cloud-init)">
        <p>
          Reads the VM&rsquo;s cloud-init network config, writes the new address
          (keeping the prefix and gateway unless you type them), then updates
          WiCloud&rsquo;s record. A refusal or no answer changes nothing.
        </p>
        <p>
          <b>It does not restart the VM</b>: the guest keeps its old address
          until the next restart. Tick the password option to also set a new OS
          password, shown once in the result.
        </p>
      </HelpSection>
      <HelpSection title="It never">
        <p>· emails the customer or posts to their activity log</p>
        <p>· touches billing, the expiry date or firewall rules</p>
        <p>
          · checks that no other VM holds the address — confirm with the
          infrastructure team
        </p>
      </HelpSection>
    </HelpPopover>
  );
}
